import React, {useState, useEffect, useCallback} from 'react';
import DowngradeModal from '../journal/DowngradeModal';
import {planAPI, trainingPreferencesAPI, todayWeatherAdjustmentAPI, athletesAPI, workoutsAPI} from '../../api';
import { useTranslation } from '../../i18n';
import { toast } from '../common/Notifier';

import {
  formatWorkoutBreakdown,
  formatWorkoutDetails,
  formatActualResult,
} from "../../utils/formatWorkout";
import { WEEKDAY_KEYS } from "../../constants/preferences";
import { useAuth } from '../../contexts/AuthContext';
import {Link} from "react-router-dom";
import {getHrRangeForType} from "../../utils/HrRangeForType";

const isSunday = new Date().getDay() === 0;
const TODAY_WEEKDAY = (new Date().getDay() + 6) % 7;

export const WeekPlanCard = ({ athleteId, refreshTrigger }) => {
  const {t, language} = useTranslation();
  const {athlete} = useAuth();
  const [weekOffset, setWeekOffset] = useState(0);
  const [downgradeWorkout, setDowngradeWorkout] = useState(null);
  const [plan, setPlan] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [extraRestWeekdays, setExtraRestWeekdays] = useState([]);
  const [showRestEditor, setShowRestEditor] = useState(false);
  const [expandedDate, setExpandedDate] = useState(null);
  const [weatherModal, setWeatherModal] = useState({open: false, loading: false, data: null, error: null});
  const [volumeWarningAcknowledged, setVolumeWarningAcknowledged] = useState(false);
  const [rescheduling, setRescheduling] = useState(null);
  const hasManualReschedules = plan?.days?.some((d) => d.is_manually_rescheduled);

  const handleAcknowledgeVolumeWarning = async (e) => {
    const checked = e.target.checked;
    setVolumeWarningAcknowledged(checked);
    try {
      await athletesAPI.acknowledgeVolumeWarning(checked);
      await loadPlan(weekOffset);
    } catch {
      setVolumeWarningAcknowledged(!checked);
      toast.error(t('error.general'));
    }
  };

  const handleDowngrade = async (workoutId, payload) => {
    try {
      await workoutsAPI.downgrade(workoutId, payload);
      toast.success(t('downgrade.success_message'));
      setDowngradeWorkout(null);
      await loadPlan(weekOffset);
    } catch (e) {
      toast.error(e.response?.data?.detail || t('error.general'));
    }
  };

  const getWeatherIcon = (day) => {
    if (!day.weather) return '🌤️';

    const weatherMap = {
      'sunny': '☀️',
      'cloudy': '☁️',
      'rainy': '🌧️',
      'snowy': '❄️',
      'stormy': '⛈️',
      'windy': '💨',
      'partly_cloudy': '⛅'
    };

    return weatherMap[day.weather] || '🌤️';
  };

  const handleStartReschedule = (date) => {
    setRescheduling(rescheduling === date ? null : date);
  };

  const handleSwapWith = async (targetDate) => {
    if (!rescheduling) return;
    try {
      await workoutsAPI.swapWorkoutDays(rescheduling, targetDate);
      toast.success(t('dashboard.days_swapped'));
      setRescheduling(null);
      await loadPlan(weekOffset);
    } catch (e) {
      toast.error(e.response?.data?.error || t('error.general'));
    }
  };

  const getWeekdayShort = (day) => {
    if (!day?.date) return '';
    const d = new Date(`${day.date}T00:00:00`);
    if (isNaN(d.getTime())) return '';
    const locale = {uk: 'uk-UA', ru: 'ru-RU', en: 'en-GB'}[language] || 'en-GB';
    return d.toLocaleDateString(locale, {weekday: 'short'});
  };

  const openWeatherModal = async () => {
    setWeatherModal({open: true, loading: true, data: null, error: null, noWorkout: false, noCity: false});
    try {
      const prefsRes = await trainingPreferencesAPI.get(athleteId);
      const {latitude, longitude, city, country} = prefsRes.data;
      const hasCity = !!(latitude && longitude);

      const {data} = await todayWeatherAdjustmentAPI.get(athleteId, latitude, longitude);
      setWeatherModal({
        open: true,
        loading: false,
        data: {...data, city, country},
        error: null,
        noWorkout: false,
        noCity: !hasCity,
      });
    } catch (error) {
      const isNoWorkout = error.response?.status === 404 && error.response?.data?.error === 'no workout scheduled for today';
      setWeatherModal({
        open: true,
        loading: false,
        data: null,
        error: isNoWorkout ? null : (error.response?.data?.error || t('error.general')),
        noWorkout: isNoWorkout,
        noCity: false,
      });
    }
  };

  const loadPlan = useCallback(async (offset) => {
    setLoading(true);
    setNotFound(false);
    try {
      const {data} = await planAPI.get(athleteId, offset);
      setPlan(data);
    } catch (error) {
      if (error.response?.status === 404) {
        setPlan(null);
        setNotFound(true);
      } else {
        toast.error(t('error.general'));
      }
    } finally {
      setLoading(false);
    }
  }, [athleteId, t]);

  const closeWeatherModal = () => setWeatherModal({open: false, loading: false, data: null, error: null})

  const getTodayDateStr = () => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const TODAY_DATE_STR = getTodayDateStr();

  const toggleExpand = (date) => {
    setExpandedDate((prev) => (prev === date ? null : date));
  };

  useEffect(() => {
    setExtraRestWeekdays([]);
    setShowRestEditor(false);
    loadPlan(weekOffset);
  }, [weekOffset, refreshTrigger, loadPlan]);

  useEffect(() => {
    if (plan) {
      setVolumeWarningAcknowledged(!!plan.volume_warning_acknowledged);
    }
  }, [plan?.volume_warning_acknowledged]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await planAPI.generate(athleteId, weekOffset, extraRestWeekdays);

      await loadPlan(weekOffset);
      setPlan(prev => ({
        ...prev,
        is_saved: true
      }));

      toast.success(t('dashboard.plan_is_saved'));
    } catch (error) {
      toast.error(error.response?.data?.error || t('error.general'));
    } finally {
      setSaving(false);
    }
  };

  const handleGenerate = async () => {
    setLoading(true);
    try {
      await planAPI.generate(athleteId, weekOffset, extraRestWeekdays);
      await loadPlan(weekOffset);
      setNotFound(false);
      toast.success(t('dashboard.plan_generated'));
    } catch (error) {
      toast.error(error.response?.data?.error || t('error.general'));
    } finally {
      setLoading(false);
    }
  };

  const toggleExtraRestDay = (day) => {
    setExtraRestWeekdays((prev) =>
        prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const canToggleDay = (day) => weekOffset !== 0 || day >= TODAY_WEEKDAY;

  // Безопасная функция для рендеринга notes
  const renderNotes = (notes) => {
    if (!notes) return null;
    const notesArray = Array.isArray(notes) ? notes : [notes];
    return notesArray.map((note, i) => (
        <div key={`note-${i}`} className="text-xs text-gray-500 italic mt-1">{note}</div>
    ));
  };

  return (
      <div className="bg-white rounded-xl shadow p-4 space-y-3">
        {/* Шапка: недели + итог + статус + действия */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Слева: недели */}
          <div className="flex-1 flex gap-1">
            {[[-1, 'prev_week'], [0, 'this_week'], [1, 'next_week']].map(([off, key]) => (
              <button
                key={off}
                onClick={() => setWeekOffset(off)}
                disabled={off === 1 && !isSunday}
                className={`px-3 py-1 rounded-lg text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed ${
                  weekOffset === off ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
                }`}
              >
                {t(`dashboard.${key}`)}
              </button>
            ))}
          </div>

          {/* Центр: отметить выходные */}
          {!loading && plan?.days && weekOffset >= 0 && (
            <button
              type="button"
              onClick={() => setShowRestEditor((v) => !v)}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              {showRestEditor ? t('dashboard.hide_rest_editor') : t('dashboard.mark_rest_days')}
            </button>
          )}

          {/* Справа: итог, статус, сохранить */}
          <div className="flex-1 flex flex-wrap items-center justify-end gap-3 text-sm">
            {!loading && plan?.days && (
              <>
                {plan.volume_breakdown && (
                  <span className="font-semibold text-gray-700">
                    {plan.computed_weekly_total_km} {t('common.km')}
                  </span>
                )}
                <span className="text-gray-500">
                  {plan.is_saved ? `✅ ${t('dashboard.plan_is_saved')}` : `⏳ ${t('dashboard.plan_not_saved')}`}
                </span>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg font-semibold text-sm disabled:opacity-50"
                >
                  {saving ? t('common.loading') : `💾 ${t('dashboard.save_plan')}`}
                </button>
              </>
            )}
          </div>
        </div>

        {loading && <div className="text-gray-500 text-sm">{t('common.loading')}</div>}

        {!loading && notFound && (
            <div className="text-gray-400 text-sm">{t('dashboard.no_week_data')}</div>
        )}

        {!loading && plan && !plan.is_saved && (
            <div className="text-xs text-amber-700 bg-amber-50 p-2 rounded">
              {t('dashboard.schedule_reset_banner')}
            </div>
        )}

        {/* Редактор выходных дней */}
        {!loading && plan && weekOffset >= 0 && showRestEditor && (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-2">
                {WEEKDAY_KEYS.map((d) => (
                    <button
                        type="button"
                        key={d.value}
                        disabled={!canToggleDay(d.value)}
                        onClick={() => toggleExtraRestDay(d.value)}
                        className={`px-3 py-1 rounded-lg text-sm font-medium transition-all disabled:opacity-30 disabled:cursor-not-allowed ${
                            extraRestWeekdays.includes(d.value)
                                ? 'bg-orange-500 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                      {t(`preferences.weekday_${d.key}`)}
                    </button>
                ))}
              </div>

              {hasManualReschedules && (
                  <div className="text-xs text-amber-700 bg-amber-50 p-2 rounded">
                    ⚠️ {t('dashboard.regenerate_wipes_reschedules')}
                  </div>
              )}

              <button
                  onClick={handleGenerate}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded-lg font-semibold text-sm"
              >
                {t('dashboard.regenerate_plan')}
              </button>
            </div>
        )}

        {!loading && plan?.error_code === 'insufficient_volume' && (
            <div className="text-sm text-red-700 bg-red-50 p-3 rounded">
              {t('readiness.blocked', {
                minimum: plan.readiness.minimum,
                current: plan.readiness.current,
              })}
            </div>
        )}

        {!loading && plan?.days && (
            <>
              {plan.readiness_warning && (
                  <div className="text-xs text-amber-700 bg-amber-50 p-2 rounded">
                    {t('readiness.warning', {
                      recommended: plan.readiness_warning.recommended,
                      current: plan.readiness_warning.current,
                    })}
                  </div>
              )}

              {(plan.readiness_status || plan.readiness_warning) && (
                  <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={volumeWarningAcknowledged}
                        onChange={handleAcknowledgeVolumeWarning}
                    />
                    {t('readiness.acknowledge_checkbox')}
                  </label>
              )}

              {plan.volume_adjustment && (
                  <div className="text-xs text-gray-500">
                    {t('dashboard.volume_adjustment_note', {
                      target: plan.volume_adjustment.target_km,
                      reduction: plan.volume_adjustment.reduction_pct,
                      phase: t(`phase.${plan.volume_adjustment.phase}`),
                    })}
                  </div>
              )}

              {/* Дни недели */}
              <div className="space-y-2">
                {plan.days.map((day) => {
                  const isExpanded = expandedDate === day.date;
                  const isToday = day.date === TODAY_DATE_STR;
                  const hr = getHrRangeForType(athlete, day.type);
                  const breakdown = formatWorkoutBreakdown(day);
                  const hasBreakdown =
                      breakdown.parts.length > 0 ||
                      (breakdown.notes &&
                          (Array.isArray(breakdown.notes) ? breakdown.notes.length > 0 : true));
                  const details = formatWorkoutDetails(day);
                  const actual = formatActualResult(day);

                  return (
                      <div
                          key={day.date}
                            className={`rounded overflow-hidden ${
                              isToday ? 'bg-blue-50 ring-2 ring-blue-400' : 'bg-gray-50'
                            }`}
                      >
                        <div
                            className={`px-3 py-2 flex items-center gap-3 ${
                                hasBreakdown || isToday ? 'cursor-pointer hover:bg-gray-100' : ''
                            }`}
                            onClick={() => {
                              if (day.is_completed && hasBreakdown) {
                                toggleExpand(day.date);
                                return;
                              }
                              if (isToday && !day.is_completed) {
                                openWeatherModal();
                                return;
                              }
                              if (hasBreakdown) {
                                toggleExpand(day.date);
                              }
                            }}
                        >
                          {/* Дата */}
                          <div className="w-12 shrink-0 text-xs text-gray-500 leading-tight">
                            <div className="font-semibold text-gray-700 capitalize">{getWeekdayShort(day)}</div>
                            <div>{day.date.slice(5)}</div>
                          </div>

                          {/* Основное */}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium flex items-center flex-wrap gap-x-2">
                              {day.label}
                              {isToday && (
                                  <span className="text-xs font-semibold text-blue-600">
                          {t('dashboard.today')}
                        </span>
                              )}
                              {day.is_manually_rescheduled && (
                                  <span
                                      className="text-xs text-red-600"
                                      title={t('dashboard.manually_rescheduled_hint')}
                                  >
                          🚩
                        </span>
                              )}
                              {day.is_completed && (
                                  <span className="text-xs text-green-600">
                          ✓ {actual || t('common.completed')}
                        </span>
                              )}
                            </div>

                            {(details || hr) && (
                                <div className="text-xs text-gray-600 truncate">
                                  {details}
                                  {hr && (
                                      <span className="text-red-500 ml-2">
                            ❤️ {hr.low}–{hr.high}
                          </span>
                                  )}
                                </div>
                            )}
                          </div>

                          {/* Справа: действия, км, стрелка */}
                          <div className="flex items-center gap-2 shrink-0">
                            {!day.is_completed && day.id && day.type !== 'easy' && day.type !== 'rest' && (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setDowngradeWorkout(day);
                                    }}
                                    className="text-xs bg-amber-50 hover:bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded-full"
                                    title={t('downgrade.button_hint')}
                                >
                                  ⬇️ {t('downgrade.button')}
                                </button>
                            )}

                            {!day.is_completed && day.type !== 'rest' && (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (rescheduling && rescheduling !== day.date) {
                                        handleSwapWith(day.date);
                                      } else {
                                        handleStartReschedule(day.date);
                                      }
                                    }}
                                    className={`text-xs px-1.5 py-0.5 rounded-full font-medium transition-colors ${
                                        rescheduling === day.date
                                            ? 'bg-blue-600 text-white'
                                            : rescheduling
                                                ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                                                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                    }`}
                                >
                                  {rescheduling === day.date
                                      ? `✕ ${t('dashboard.cancel_reschedule')}`
                                      : rescheduling
                                          ? `↳ ${t('dashboard.swap_here')}`
                                          : '🔀'}
                                </button>
                            )}

                            {day.distance_km && (
                                <span className="text-sm text-gray-600 whitespace-nowrap">
                        {day.distance_km} {t('common.km')}
                      </span>
                            )}

                            {(hasBreakdown || isToday) && (
                                <span className="text-xs text-gray-400">
                        {isToday ? getWeatherIcon(day) : isExpanded ? '▲' : '▼'}
                      </span>
                            )}
                          </div>
                        </div>

                        {/* Раскрытая часть */}
                        {isExpanded && hasBreakdown && (
                            <div className="px-3 pb-3 pt-1 border-t border-gray-200 text-sm space-y-1">
                              {breakdown.parts.map((p, i) => (
                                  <div key={i} className="flex justify-between text-gray-600">
                                    <span>{p.label}</span>
                                    <span>{p.value}</span>
                                  </div>
                              ))}
                              {renderNotes(breakdown.notes)}
                              {day.is_completed && (
                                  <Link
                                      to={`/workouts?date=${day.date}`}
                                      className="flex justify-between items-center text-xs mt-2 pt-2 border-t border-gray-200 hover:bg-gray-50 -mx-1 px-1 rounded transition-colors"
                                      onClick={(e) => e.stopPropagation()}
                                  >
                        <span className="text-blue-600">
                          {t('dashboard.plan_vs_actual', {
                            planned: day.distance_km != null ? `${day.distance_km} ${t('common.km')}` : '—',
                            actual: actual ?? '—',
                          })}
                        </span>
                                    <span className="text-blue-600 font-medium whitespace-nowrap ml-2">
                          {t('dashboard.view_analysis')} →
                        </span>
                                  </Link>
                              )}
                            </div>
                        )}
                      </div>
                  );
                })}
              </div>

              {/* Модалка погоды */}
              {weatherModal.open && (
                  <div
                      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
                      onClick={closeWeatherModal}
                  >
                    <div
                        className="bg-white rounded-xl shadow-lg p-6 max-w-sm w-full space-y-3"
                        onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex justify-between items-center">
                        <h3 className="text-lg font-semibold">{t('dashboard.today_weather_title')}</h3>
                        <button onClick={closeWeatherModal} className="text-gray-400 hover:text-gray-600">✕</button>
                      </div>

                      {weatherModal.loading && (
                          <div className="text-gray-500 text-sm">{t('common.loading')}</div>
                      )}

                      {!weatherModal.loading && weatherModal.error && (
                          <div className="text-sm text-red-600">{weatherModal.error}</div>
                      )}

                      {!weatherModal.loading && weatherModal.data && (
                          <div className="space-y-2 text-sm">
                            {weatherModal.noCity && (
                                <div className="text-sm text-amber-700 bg-amber-50 p-3 rounded">
                                  {t('dashboard.no_city_warning')}
                                </div>
                            )}

                            <div>
                              <span className="text-gray-500">{t('dashboard.your_city')}: </span>
                              <span className="font-medium">
                      {weatherModal.data.city
                          ? `${weatherModal.data.city}${weatherModal.data.country ? `, ${weatherModal.data.country}` : ''}`
                          : '—'}
                    </span>
                            </div>

                            <div>
                              <span className="text-gray-500">{t('dashboard.weather')}: </span>
                              <span className="font-medium">
                      {weatherModal.data.weather
                          ? `${weatherModal.data.weather.temperature}°C${weatherModal.data.weather.dew_point != null ? ` · td ${weatherModal.data.weather.dew_point}°C` : ''}`
                          : '—'}
                    </span>
                            </div>

                            {weatherModal.data.adjustment?.segments ? (
                                <div className="border-t pt-2 space-y-1">
                                  {weatherModal.data.adjustment.warmup_km && (
                                      <div className="flex justify-between">
                                        <span className="text-gray-500">{t('workouts.warmup')}</span>
                                        <span>{weatherModal.data.adjustment.warmup_km}km</span>
                                      </div>
                                  )}
                                  {weatherModal.data.adjustment.segments.map((seg, i) => (
                                      <div key={i} className="flex justify-between">
                                        <span className="text-gray-500">{seg.label}</span>
                                        <span>
                            {seg.distance_km}km @{' '}
                                          <span className="line-through text-gray-400">{seg.base_pace}</span>{' '}
                                          <span className="font-semibold text-orange-600">{seg.adjusted_pace}</span>/км
                          </span>
                                      </div>
                                  ))}
                                  {weatherModal.data.adjustment.cooldown_km && (
                                      <div className="flex justify-between">
                                        <span className="text-gray-500">{t('workouts.cooldown')}</span>
                                        <span>{weatherModal.data.adjustment.cooldown_km}km</span>
                                      </div>
                                  )}
                                  <div className="border-t pt-2 flex justify-between">
                                    <span className="text-gray-500">{t('dashboard.pace_adjustment')}: </span>
                                    <span className="font-medium text-orange-600">
                          +{weatherModal.data.adjustment.penalty_pct}%
                        </span>
                                  </div>
                                </div>
                            ) : weatherModal.data.workout?.pace ? (
                                <>
                                  <div>
                                    <span className="text-gray-500">{t('dashboard.base_pace')}: </span>
                                    <span className="font-medium">{weatherModal.data.workout.pace} /км</span>
                                  </div>
                                  <div>
                                    <span className="text-gray-500">{t('dashboard.pace_adjustment')}: </span>
                                    <span className="font-medium text-orange-600">
                          {weatherModal.data.adjustment ? `+${weatherModal.data.adjustment.penalty_pct}%` : '—'}
                        </span>
                                  </div>
                                  <div className="border-t pt-2">
                                    <span className="text-gray-500">{t('dashboard.today_total')}: </span>
                                    <span className="font-semibold text-blue-600">
                          {weatherModal.data.adjustment
                              ? weatherModal.data.adjustment.adjusted_pace
                              : weatherModal.data.workout.pace} /км
                        </span>
                                  </div>
                                </>
                            ) : (
                                <div className="text-gray-400">{t('dashboard.no_pace_adjustment')}</div>
                            )}
                          </div>
                      )}
                    </div>
                  </div>
              )}

              <DowngradeModal
                  workout={downgradeWorkout ? {...downgradeWorkout, workout_type: downgradeWorkout.type} : null}
                  isOpen={Boolean(downgradeWorkout)}
                  onClose={() => setDowngradeWorkout(null)}
                  onConfirm={handleDowngrade}
              />
            </>
        )}
      </div>
  );
}